/**
 * Good to Start for one-click and hotkey orders.
 *
 * Until this module, every one-click / hotkey Kalshi order (mouse pill or
 * ladder click, A/D, W/E Attack, both legs of S / 2 / 3 / Shift+S) went to the
 * venue with NO expiry, so a pregame press rested into live play even while
 * the Game Detail ticket beside it read GOOD TO START. The ticket is the only
 * path that ever sent one.
 *
 * The contract now: before the start, a press uses the same duration the
 * trader sees — the Game Detail ticket's own Kalshi selection on Game Detail,
 * the Settings default everywhere else — and the expiry is computed by the
 * ticket's own `resolveExpiry` / `expirationTimeFrom`, from the same event
 * start the ticket uses (`getEventStartTimeMsByTicker`: Hydra eventStartTime
 * with Kalshi milestone precedence). GTS with no trustworthy future start
 * refuses the press, named, exactly as the ticket refuses to submit; it never
 * becomes a silent GTC. Once the market's game is live (the app's own derived
 * game state for that market), a press is GTC, as one-click always was.
 *
 * The decision is made per order at submit time, so a two-sided press asks it
 * for each leg and Attack asks it like any other press.
 */
import { create } from 'zustand';
import { useDisplaySettings } from '@/stores/displaySettings';
import {
  deriveGameStateWithAuthority,
  formatStartTimeLocal,
  useMarketStore,
} from '@/stores/marketStore';
import { expirationTimeFrom, resolveExpiry } from '@/views/ValueDashboard/orderEntryPayloads';
import type { Lt2Surface } from './liveIntent';

export type OneClickExpiryMode = 'gts' | 'gtc' | 'custom';

/** Where a press's duration comes from: the Game Detail ticket's visible
 *  Kalshi selection, or the Settings default the ticket would open on. */
export interface OneClickExpirySelection {
  mode: OneClickExpiryMode;
  /** datetime-local value; only meaningful with mode 'custom'. */
  customExpiry: string;
  source: 'ticket' | 'settings';
}

export type OneClickDuration =
  /** The press carries a venue expiry. */
  | { ok: true; kind: 'gts' | 'gtd'; expirationMs: number; expirationTime: string }
  /** No expiry: GTC was selected, or the game is already live. */
  | { ok: true; kind: 'gtc'; reason: 'selected' | 'live' }
  /** The selected duration cannot be honored right now: nothing is sent. */
  | { ok: false; kind: 'gts' | 'gtd'; message: string };

/** Where a refusal tells the trader to change the duration. */
function fixHint(source: OneClickExpirySelection['source']): string {
  return source === 'ticket'
    ? 'pick GTC on the ticket to place now'
    : 'set the default expiry to GTC in Settings to place now';
}

/**
 * Pure: the duration one press uses. `started` is the market's game being
 * live (or over) by the app's own game-state rule; `startMs` is the event
 * start the ticket would use. The expiry arithmetic is the ticket's.
 */
export function resolveOneClickDuration(args: {
  selection: OneClickExpirySelection;
  startMs: number | null;
  started: boolean;
  nowMs: number;
}): OneClickDuration {
  const { selection, startMs, started, nowMs } = args;
  // Live play is what one-click is for: no start to expire at any more.
  if (started) return { ok: true, kind: 'gtc', reason: 'live' };
  if (selection.mode === 'gtc') return { ok: true, kind: 'gtc', reason: 'selected' };
  const kind = selection.mode === 'gts' ? 'gts' : 'gtd';
  const resolved = resolveExpiry({
    expiryMode: selection.mode,
    gameStartMs: startMs,
    customExpiry: selection.customExpiry,
    nowMs,
  });
  if (resolved.ok && resolved.expirationMs != null) {
    const expirationTime = expirationTimeFrom(resolved.expirationMs);
    if (expirationTime != null) {
      return { ok: true, kind, expirationMs: resolved.expirationMs, expirationTime };
    }
  }
  const hint = fixHint(selection.source);
  if (kind === 'gts') {
    return {
      ok: false,
      kind,
      message:
        startMs == null
          ? `GTS: event start unknown — not sent; ${hint}`
          : `GTS: event start has passed but the game is not live — not sent; ${hint}`,
    };
  }
  return {
    ok: false,
    kind,
    message: selection.customExpiry
      ? `Custom expiry has passed — not sent; ${selection.source === 'ticket' ? 'pick a later time or GTC on the ticket' : hint}`
      : `Custom expiry has no time outside the ticket — not sent; ${hint}`,
  };
}

// ── The Game Detail ticket's visible selection ─────────────────────────────

interface TicketExpiryState {
  /** The Game Detail ticket's Kalshi expiry selection, while that ticket is
   *  mounted. Null = no Game Detail ticket: the Settings default applies. */
  ticket: { mode: OneClickExpiryMode; customExpiry: string; owner: number } | null;
}

/** Published by the Game Detail order ticket (useOrderEntryModel with
 *  `publishOneClickExpiry`), read by every Game Detail one-click press and
 *  the Game Detail strip's duration badge. */
export const useOneClickTicketExpiry = create<TicketExpiryState>(() => ({ ticket: null }));

let _ownerSeq = 0;

/** A new publisher's identity; its clear only removes its own publication. */
export function nextTicketExpiryOwner(): number {
  return ++_ownerSeq;
}

export function publishTicketExpiry(
  owner: number,
  mode: OneClickExpiryMode,
  customExpiry: string,
): void {
  const current = useOneClickTicketExpiry.getState().ticket;
  if (
    current != null &&
    current.owner === owner &&
    current.mode === mode &&
    current.customExpiry === customExpiry
  ) {
    return;
  }
  useOneClickTicketExpiry.setState({ ticket: { mode, customExpiry, owner } });
}

export function clearTicketExpiry(owner: number): void {
  if (useOneClickTicketExpiry.getState().ticket?.owner === owner) {
    useOneClickTicketExpiry.setState({ ticket: null });
  }
}

/** A Game Detail surface follows the Game Detail ticket. */
export function isGameDetailSurface(surface: Lt2Surface | null | undefined): boolean {
  return surface != null && surface.startsWith('gd-');
}

/** The selection a press on `surface` uses. */
export function oneClickExpirySelection(
  surface: Lt2Surface | null | undefined,
  ticket: TicketExpiryState['ticket'] = useOneClickTicketExpiry.getState().ticket,
  defaultMode: OneClickExpiryMode = useDisplaySettings.getState().defaultExpiryMode,
): OneClickExpirySelection {
  if (isGameDetailSurface(surface) && ticket != null) {
    return { mode: ticket.mode, customExpiry: ticket.customExpiry, source: 'ticket' };
  }
  return { mode: defaultMode, customExpiry: '', source: 'settings' };
}

/**
 * The clicked market's event start (the ticket's source, through the
 * precedence cache) and whether its game has started, by the same
 * `deriveGameStateWithAuthority` rule every game row uses, applied to this
 * market's own status. An unknown start is never "started": the app's rule
 * calls such a game pregame.
 */
export function oneClickGameClock(
  ticker: string,
  nowMs: number,
): { startMs: number | null; started: boolean } {
  const store = useMarketStore.getState();
  const startMs = ticker ? store.getEventStartTimeMsByTicker(ticker) : null;
  const market = ticker ? store.getMarket(ticker) : null;
  const statuses = market?.status ? [market.status] : [];
  const state = deriveGameStateWithAuthority(statuses, false, startMs, nowMs, market?.sport ?? null);
  return { startMs, started: state.gameState !== 'pre' };
}

/** The duration a press at `ticker` on `surface` uses right now. */
export function resolvePressDuration(
  intent: { ticker: string; surface?: Lt2Surface | null },
  nowMs: number = Date.now(),
): OneClickDuration {
  const { startMs, started } = oneClickGameClock(intent.ticker, nowMs);
  return resolveOneClickDuration({
    selection: oneClickExpirySelection(intent.surface),
    startMs,
    started,
    nowMs,
  });
}

/** The badge face for a resolved duration: "GTS", "GTC", "GTD 7:45p", or the
 *  refused mode with a cross. */
export function durationBadgeText(duration: OneClickDuration): string {
  if (!duration.ok) return `${duration.kind.toUpperCase()} ✗`;
  if (duration.kind === 'gtc') return 'GTC';
  if (duration.kind === 'gts') return 'GTS';
  return `GTD ${formatStartTimeLocal(duration.expirationMs)}`;
}
